import React from "react";
import { createContext, useState, useContext } from "react";
import AlertDialog from "../presentation/components/Dialog";
import { useDidMountEffect } from "../hooks/useDidMountEffect";
import axios from 'axios'
import { encrypt, decrypt, isEmpty } from "../helpers/util";
import theme from "../presentation/theme";
import { CircularProgress } from "@mui/material";

export const AppContext = createContext({});

export function AppProvider({ children }) {

    const MODE = import.meta.env.MODE
    const permissionKey = import.meta.env.VITE_P_D_K
    const accessTokenInitialState = MODE === "development" ? import.meta.env.VITE_JWT_TOKEN_DEV : ""
    const icInitialState = MODE === "development" ? encrypt(import.meta.env.VITE_ID_CONTATO, permissionKey) : ""
    const ideInitialState = MODE === "development" ? encrypt(import.meta.env.VITE_ID_EMPRESA, permissionKey) : ""

    const [accessToken, setAccessToken] = useState(accessTokenInitialState)
    const [ic, setIc] = useState(icInitialState)
    const [ide, setIde] = useState(ideInitialState)
    const [userCountry, setUserCountry] = useState("Brasil")
    const [language, setLanguage] = useState("PT-BR")
    const [translations, setTranslations] = useState([])
    const [loading, setLoading] = useState(false)
    const [loadingToken, setLoadingToken] = useState(false)
    const [buildingCountry, setBuildingCountry] = useState("Brasil")

    const [dialogProps, setDialogProps] = useState({
        open: false
    })

    const phpAPIBuildings = axios.create({
        baseURL: "https://api.buildings.com.br",
    })

    const getNexusUrl = () => {
        if (MODE === "development") {
            return "http://localhost:8080"
        } else {
            return import.meta.env.VITE_NEXUS_URL
        }
    }

    const nexusApi = axios.create({
        baseURL: getNexusUrl(),
        headers: {
            'Authorization': 'Bearer ' + accessToken,
            "Content-Type": "application/json",
            "Cache-Control": 36000
        }
    })

    nexusApi.interceptors.request.use((config) => {
        config.params = config.params || {};
        if (MODE === "development") {
            config.headers['ic'] = ic;
            config.headers['ide'] = ide;
        }
        return config;
    });

    useDidMountEffect(() => {
        window.parent.postMessage({ loaded: "1" }, "*")
        window.parent.postMessage({ loadingMask: "0" }, "*")
        window.addEventListener('message', listenerMessageFunction);
    }, [])

    useDidMountEffect(() => {
        if (!isEmpty(accessToken) && !isEmpty(ic)) {
            getEncryptPermissions()
        }
    }, [accessToken, ic])

    const translateBD = async () => {
        await nexusApi.get(`/dashboard/getTraducaoCREToolNexus?type=cretool`).then((response) => {
            setTranslations(response.data.data)
        })
    }

    const getEncryptPermissions = async () => {
        try {
            const response = await nexusApi.get(`/gup`)
            const { p: permissoes } = response.data
            const decryptedPermissoes = JSON.parse(decrypt(permissoes, permissionKey));
            setUserCountry(decryptedPermissoes.userCountry)
            await translateBD()
            setLoadingToken(false)
        } catch (error) {
            return
        }
    }

    const listenerMessageFunction = (event) => {
        setLoadingToken(true)
        handleSetUserInfo(event.data)
        window.removeEventListener("message", listenerMessageFunction)
    }

    const handleSetUserInfo = (postData) => {
        if (postData.id_contato) {
            setIc(encrypt(postData.id_contato, permissionKey))
            setLanguage(formatLanguage(postData.language))
            if (accessToken === "") {
                setAccessToken(postData.access_token)
                setRenovateAccessToken(postData.access_token, postData.id_contato)
            }

            if (ide === "") {
                setIde(encrypt(postData.id_empresa, permissionKey))
            }
        }
    }

    const renovateAccessToken = (new_access_token, id_contato) => {
        phpAPIBuildings.get(`/renovateCretoolAccessToken?ic=${id_contato}&access_token=${new_access_token}`).then(response => {
            const { userAccessToken } = response.data
            setAccessToken(userAccessToken)
            setRenovateAccessToken(userAccessToken, id_contato)
        })
    }

    const setRenovateAccessToken = (new_access_token, id_contato) => {
        setTimeout(() => {
            renovateAccessToken(new_access_token, id_contato)
        }, 3540000); // 59 min
    }

    const formatLanguage = (language) => {
        switch (language) {
            case "en":
                return "EN"
            case "es":
                return "ES"
            default:
                return "PT-BR"
        }
    }

    const showDialog = (props) => {
        setDialogProps({ open: true, ...props })
    }

    const closeDialog = () => {
        setDialogProps({ open: false })
    }

    return (
        <AppContext.Provider value={{ loading, setLoading, showDialog, closeDialog, nexusApi, userCountry, language, translations, buildingCountry, setBuildingCountry }}>
            {!loadingToken && children}
            {loadingToken &&
                <div
                    style={{
                        backgroundColor: theme.palette.background.tab,
                        display: "flex",
                        flexDirection: 'column',
                        minWidth: "100%",
                        minHeight: "100%",
                        justifyContent: "center",
                        alignItems: "center"
                    }}
                >
                    <CircularProgress color='primary' />
                </div>
            }
            {dialogProps.open && <AlertDialog {...dialogProps} handleClose={closeDialog} />}
        </AppContext.Provider>
    )
};

export function useApp() {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error("useLoading must be used within LoadingProvider");
    }
    return context;
}